// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight, ArrowRight, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { fetchCallEngineTraces } from '@/service/callEngineTraces';

export function isSuperAdminUser() {
  return getUser()?.role === USER_ROLES.SUPERADMIN;
}

export function resolveCallEngineTraceId({
  sipCallId,
  callSid,
  row,
}: {
  sipCallId?: string;
  callSid?: string;
  row?: any;
} = {}) {
  const sip = String(sipCallId ?? row?.sip_call_id ?? '').trim();
  if (sip) return sip;
  return String(callSid ?? row?.call_sid ?? '').trim();
}

export function isSipTraceLookup({
  sipCallId,
  callSid,
  row,
}: {
  sipCallId?: string;
  callSid?: string;
  row?: any;
} = {}) {
  return Boolean(String(sipCallId ?? row?.sip_call_id ?? '').trim());
}

function formatTraceTime(value: any) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour12: false,
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatTraceMillis(value: any) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

function parseSipHeader(payload: string, name: string) {
  if (!payload) return '';
  const re = new RegExp(`^${name}:\\s*(.+)$`, 'im');
  const m = payload.match(re);
  return m ? m[1].trim() : '';
}

function endpointLabel(ip: string, port: any, payload: string, side: 'src' | 'dst') {
  if (!ip || ip === '0.0.0.0') return { title: 'SIP Proxy', sub: port ? `:${port}` : 'capture' };
  const ua = parseSipHeader(payload, 'User-Agent');
  const server = parseSipHeader(payload, 'Server');
  const hint = side === 'src' ? ua : server || ua;
  let title = `${ip}`;
  if (/asterisk/i.test(hint)) title = 'Asterisk';
  else if (/kamailio/i.test(hint)) title = 'Kamailio';
  else if (/expressivr|nspl|webrtc|sip\.js/i.test(hint)) title = 'Frontend';
  return { title, sub: `:${port || '—'}${hint ? ` · ${hint.split('(')[0].trim()}` : ''}` };
}

function eventLabel(trace: any) {
  if (trace?.method) return String(trace.method).toUpperCase();
  if (trace?.status_code != null) {
    const code = String(trace.status_code);
    const firstLine = String(trace.payload || '').split(/\r?\n/)[0] || '';
    const reason = firstLine.replace(/^SIP\/2\.0\s+\d+\s*/i, '').trim();
    return reason ? `${code} ${reason}` : code;
  }
  return trace?.cseq_method || trace?.type || 'EVENT';
}

function statusTone(trace: any) {
  const code = Number(trace?.status_code);
  if (trace?.method && !code) {
    return 'bg-sky-100 text-sky-800 ring-sky-200 dark:bg-sky-950/50 dark:text-sky-200 dark:ring-sky-800';
  }
  if (code >= 500) {
    return 'bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-950/50 dark:text-rose-200 dark:ring-rose-800';
  }
  if (code >= 400) {
    return 'bg-amber-100 text-amber-900 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-200 dark:ring-amber-800';
  }
  if (code >= 200) {
    return 'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-200 dark:ring-emerald-800';
  }
  if (code >= 100) {
    return 'bg-indigo-100 text-indigo-800 ring-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-200 dark:ring-indigo-800';
  }
  return 'bg-muted text-foreground ring-border';
}

function TraceHop({ trace }: { trace: any }) {
  const [open, setOpen] = useState(false);
  const src = endpointLabel(trace.src_ip, trace.src_port, trace.payload, 'src');
  const dst = endpointLabel(trace.dst_ip, trace.dst_port, trace.payload, 'dst');
  const label = eventLabel(trace);
  const isRequest = Boolean(trace.method) && trace.status_code == null;

  return (
    <div className="rounded-lg border border-border/60 bg-card px-3 py-2.5">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span className="font-mono tabular-nums">
          {formatTraceTime(trace.timestamp)}
          <span className="text-muted-foreground/70">{formatTraceMillis(trace.timestamp)}</span>
        </span>
        <span className="uppercase tracking-wide">
          {isRequest ? 'request sent' : 'response received'}
        </span>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
        <div className="min-w-0 rounded-md bg-muted/60 px-2.5 py-2 text-left">
          <div className="truncate text-xs font-semibold text-foreground">{src.title}</div>
          <div className="truncate font-mono text-[10px] text-muted-foreground">{src.sub}</div>
        </div>

        <div className="flex min-w-[140px] flex-col items-center gap-1">
          <span
            className={
              'inline-flex max-w-[220px] items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              statusTone(trace)
            }
            title={label}
          >
            {label}
          </span>
          <div className="flex w-full items-center text-primary">
            <span className="h-px flex-1 bg-primary/50" />
            <ArrowRight className="h-4 w-4 shrink-0" />
          </div>
          <span className="text-[10px] text-muted-foreground">
            {trace.src_ip}:{trace.src_port} → {trace.dst_ip}:{trace.dst_port}
          </span>
        </div>

        <div className="min-w-0 rounded-md bg-muted/60 px-2.5 py-2 text-right">
          <div className="truncate text-xs font-semibold text-foreground">{dst.title}</div>
          <div className="truncate font-mono text-[10px] text-muted-foreground">{dst.sub}</div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {trace.from_user ? (
          <span>
            From <span className="font-mono text-foreground">{trace.from_user}</span>
          </span>
        ) : null}
        {trace.to_user ? (
          <span>
            To <span className="font-mono text-foreground">{trace.to_user}</span>
          </span>
        ) : null}
        {trace.cseq_method ? (
          <span>
            CSeq <span className="font-mono text-foreground">{trace.cseq_method}</span>
          </span>
        ) : null}
      </div>

      {trace.payload ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-primary"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {open ? 'Hide SIP payload' : 'Show SIP payload'}
        </button>
      ) : null}

      {open && trace.payload ? (
        <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted/80 p-2.5 font-mono text-[11px] leading-relaxed text-foreground whitespace-pre-wrap">
          {String(trace.payload).replace(/\r\n/g, '\n')}
        </pre>
      ) : null}
    </div>
  );
}

export function CallEngineTracesPanel({
  callSid,
  lookupField,
}: {
  callSid: string;
  lookupField?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState<any>(null);
  const usingSipId = lookupField !== 'call_sid';

  useEffect(() => {
    const sid = String(callSid || '').trim();
    if (!sid) {
      setPayload(null);
      setError('This call has no SIP Call-ID.');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setPayload(null);
    fetchCallEngineTraces(sid)
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load traces');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [callSid]);

  const traces = useMemo(() => {
    const list = Array.isArray(payload?.traces) ? payload.traces : [];
    return [...list].sort((a, b) => {
      const ta = new Date(a?.timestamp || 0).getTime();
      const tb = new Date(b?.timestamp || 0).getTime();
      return ta - tb;
    });
  }, [payload]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading engine traces…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200">
        {error}
      </div>
    );
  }

  if (!traces.length) {
    return (
      <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
        No send/receive events recorded for this{' '}
        {usingSipId ? 'SIP Call-ID' : 'Call SID'}.
        <div className="mt-2 font-mono text-xs text-foreground">{callSid || '—'}</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {usingSipId ? 'SIP Call-ID' : 'Call SID'}{' '}
          <span className="font-mono text-foreground">{payload?.call_id || callSid}</span>
        </span>
        <span>
          {payload?.count ?? traces.length} event{(payload?.count ?? traces.length) === 1 ? '' : 's'}
        </span>
      </div>
      <div className="space-y-2">
        {traces.map((trace: any, index: number) => (
          <TraceHop key={trace.id || `${trace.timestamp}-${index}`} trace={trace} />
        ))}
      </div>
    </div>
  );
}

const TRACES_DIALOG_Z = 'z-[1100]';

function stopOpenGesture(e: any) {
  e.preventDefault();
  e.stopPropagation();
}

export function CallEngineTracesDialog({
  open,
  onOpenChange,
  callSid,
  lookupField,
  preventOutsideClose = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callSid: string;
  lookupField?: string;
  preventOutsideClose?: boolean;
}) {
  const blockOutside = (e: any) => {
    if (preventOutsideClose) e.preventDefault();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        overlayClassName={TRACES_DIALOG_Z}
        className={
          TRACES_DIALOG_Z +
          ' max-h-[88vh] w-[min(920px,calc(100vw-2rem))] max-w-4xl overflow-y-auto'
        }
        onPointerDownOutside={blockOutside}
        onInteractOutside={blockOutside}
        onFocusOutside={blockOutside}
      >
        <DialogHeader>
          <DialogTitle>Call engine traces</DialogTitle>
          <DialogDescription>
            SIP events the call engine sent and received for this call, looked up by{' '}
            {lookupField === 'call_sid' ? 'Call SID' : 'SIP Call-ID'}.
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <CallEngineTracesPanel callSid={callSid} lookupField={lookupField} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function CallEngineTracesButton({
  callSid,
  sipCallId,
  className = '',
  onOpen,
}: {
  callSid?: string;
  sipCallId?: string;
  className?: string;
  onOpen?: (id: string, field: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!isSuperAdminUser()) return null;
  const usingSip = isSipTraceLookup({ sipCallId, callSid });
  const sid = resolveCallEngineTraceId({ sipCallId, callSid });
  const field = usingSip ? 'sip_call_id' : 'call_sid';
  const lifted = typeof onOpen === 'function';

  const openTraces = (e: any) => {
    stopOpenGesture(e);
    if (!sid) return;
    if (lifted) onOpen(sid, field);
    else setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        disabled={!sid}
        onPointerDown={openTraces}
        onClick={openTraces}
        title={
          sid
            ? usingSip
              ? `View call-engine traces (SIP Call-ID ${sid})`
              : `View call-engine traces (Call SID ${sid}; no SIP Call-ID on this row)`
            : 'No SIP Call-ID on this row'
        }
        aria-label="View call-engine traces"
        className={
          'inline-flex items-center justify-center gap-1 rounded-md bg-violet-100 text-violet-800 ring-1 ring-inset ring-violet-200 transition-colors hover:bg-violet-200 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-violet-950/40 dark:text-violet-200 ' +
          className
        }
      >
        <ArrowLeftRight className="h-3.5 w-3.5" />
      </button>
      {lifted ? null : (
        <CallEngineTracesDialog
          open={open}
          onOpenChange={setOpen}
          callSid={sid}
          lookupField={field}
        />
      )}
    </>
  );
}
